// Shared building blocks for the catalog editors (items, recipes, services,
// modifiers, promotions, transfers, adjustments…).

import { ReactNode, useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography
} from '@mui/material';

import { adminApi, fetchCustomFields, type CustomFieldDefinition } from '../../api/client';
import { ensureLocation } from '../../session/location';
import { t } from '../../i18n';

export type ValidationErrors = Record<string, string[]>;

export type RemoteOption = { value: string | number; label: string; raw?: Record<string, unknown> };

/** ULID-like lowercase alphanumeric code (legacy `sku()` used ulid().toLowerCase()). */
export function makeSku(prefix = '') {
  const time = Date.now().toString(36);
  let random = '';
  while (random.length < 16) random += Math.random().toString(36).slice(2);
  return `${prefix}${time}${random}`.slice(0, 26).toLowerCase();
}

export function rowKey(prefix = 'row') {
  return `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

/** Number or null for nullable numeric inputs. */
export function numOrNull(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

export function numOr0(value: unknown): number {
  return numOrNull(value) ?? 0;
}

export function fieldError(errors: ValidationErrors, key: string) {
  return errors[key]?.[0] ?? '';
}

export function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : {};
}

export function asArray(value: unknown): Array<Record<string, unknown>> {
  return Array.isArray(value) ? (value.filter((entry) => entry && typeof entry === 'object') as Array<Record<string, unknown>>) : [];
}

export function idsOf(value: unknown): Array<string | number> {
  if (!Array.isArray(value)) return [];
  return value.flatMap((entry) => {
    if (entry == null) return [];
    if (typeof entry !== 'object') return [entry as string | number];
    const record = entry as Record<string, unknown>;
    const id = record.id ?? record.value;
    return id == null ? [] : [id as string | number];
  });
}

/** Human label for a variation meta object ({ Size: 'M', Color: 'Red' }). */
export function metaLabel(meta: unknown) {
  const record = asRecord(meta);
  return Object.entries(record)
    .map(([key, value]) => `${key}: ${String(value ?? '')}`)
    .join(', ');
}

export function useDebounced<T>(value: T, delay = 250) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

function defaultMapOption(raw: Record<string, unknown>): RemoteOption {
  const value = (raw.value ?? raw.id) as string | number;
  const label = String(raw.label ?? (raw.name ? `${raw.name}${raw.code ? ` (${raw.code})` : ''}` : value));
  return { value, label, raw };
}

type RemoteAutocompleteBase = {
  endpoint: string;
  params?: Record<string, string | number | undefined>;
  label?: string;
  placeholder?: string;
  requiresLocation?: boolean;
  mapOption?: (raw: Record<string, unknown>) => RemoteOption;
  error?: string;
  helperText?: string;
  required?: boolean;
  disabled?: boolean;
  size?: 'small' | 'medium';
  minChars?: number;
};

/** Remote search autocomplete (legacy `<Select remote filterable :remote-method>`). */
export function RemoteAutocomplete(props: RemoteAutocompleteBase & { value: RemoteOption | null; onChange: (option: RemoteOption | null) => void }) {
  return <RemoteAutocompleteInner {...props} multiple={false} />;
}

export function RemoteMultiAutocomplete(props: RemoteAutocompleteBase & { value: RemoteOption[]; onChange: (options: RemoteOption[]) => void }) {
  return <RemoteAutocompleteInner {...props} multiple />;
}

function RemoteAutocompleteInner(
  props: RemoteAutocompleteBase &
    ({ multiple: false; value: RemoteOption | null; onChange: (option: RemoteOption | null) => void } | { multiple: true; value: RemoteOption[]; onChange: (options: RemoteOption[]) => void })
) {
  const { endpoint, params, label, placeholder, requiresLocation, mapOption = defaultMapOption, error, helperText, required, disabled, size = 'small', minChars = 0 } = props;
  const [input, setInput] = useState('');
  const query = useDebounced(input, 250);
  const [options, setOptions] = useState<RemoteOption[]>([]);
  const [loading, setLoading] = useState(false);
  const paramsKey = JSON.stringify(params ?? {});

  useEffect(() => {
    if (query.length < minChars) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        if (requiresLocation) await ensureLocation();
        const response = await adminApi.get(endpoint, { params: { ...(params ?? {}), q: query } });
        const rows = Array.isArray(response.data) ? (response.data as Array<Record<string, unknown>>) : [];
        if (!cancelled) setOptions(rows.filter((row) => row && (row.value ?? row.id) != null).map(mapOption));
      } catch {
        if (!cancelled) setOptions([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpoint, paramsKey, query, requiresLocation, minChars]);

  const merged = useMemo(() => {
    const selected = props.multiple ? props.value : props.value ? [props.value] : [];
    const seen = new Set(options.map((option) => String(option.value)));
    return [...options, ...selected.filter((option) => !seen.has(String(option.value)))];
  }, [options, props.multiple, props.value]);

  const common = {
    size,
    options: merged,
    loading,
    disabled,
    filterOptions: (items: RemoteOption[]) => items,
    isOptionEqualToValue: (option: RemoteOption, value: RemoteOption) => String(option.value) === String(value.value),
    getOptionLabel: (option: RemoteOption) => option.label,
    onInputChange: (_event: unknown, value: string, reason: string) => {
      if (reason === 'input') setInput(value);
      if (reason === 'clear') setInput('');
    },
    noOptionsText: t('common.noResults'),
    renderInput: (params: object) => (
      <TextField
        {...params}
        label={label}
        placeholder={placeholder ?? t('catalog.searchItem')}
        required={required}
        error={Boolean(error)}
        helperText={error || helperText}
        InputProps={{
          ...(params as { InputProps?: object }).InputProps,
          endAdornment: (
            <>
              {loading ? <CircularProgress size={16} /> : null}
              {((params as { InputProps?: { endAdornment?: ReactNode } }).InputProps?.endAdornment) ?? null}
            </>
          )
        }}
      />
    )
  };

  if (props.multiple) {
    const onChange = props.onChange;
    return <Autocomplete multiple {...common} value={props.value} onChange={(_event, value) => onChange(value as RemoteOption[])} />;
  }
  const onChange = props.onChange;
  return <Autocomplete {...common} value={props.value} onChange={(_event, value) => { setInput(''); onChange((value as RemoteOption | null) ?? null); }} />;
}

/** Card with a title used to group editor fields. */
export function EditorSection({ title, description, action, children }: { title: string; description?: string; action?: ReactNode; children: ReactNode }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }} spacing={1}>
        <Box>
          <Typography variant="subtitle1" fontWeight={700}>{title}</Typography>
          {description ? <Typography variant="caption" color="text.secondary">{description}</Typography> : null}
        </Box>
        {action}
      </Stack>
      {children}
    </Paper>
  );
}

/** Custom fields (Settings → Fields) loaded from GET {resource}/create. */
export function useCustomFieldDefs(resource: string | null) {
  const [defs, setDefs] = useState<CustomFieldDefinition[]>([]);
  useEffect(() => {
    if (!resource) return;
    let cancelled = false;
    fetchCustomFields(resource).then((list) => !cancelled && setDefs(list)).catch(() => !cancelled && setDefs([]));
    return () => {
      cancelled = true;
    };
  }, [resource]);
  return defs;
}

export function CustomFieldsEditor({
  defs,
  values,
  errors,
  onChange
}: {
  defs: CustomFieldDefinition[];
  values: Record<string, unknown>;
  errors?: ValidationErrors;
  onChange: (slug: string, value: unknown) => void;
}) {
  if (!defs.length) return null;
  return (
    <EditorSection title={t('crud.customFields')}>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
        {defs.map((definition) => (
          <CustomFieldControl
            key={definition.slug}
            definition={definition}
            value={values[definition.slug]}
            error={errors ? fieldError(errors, `extra_attributes.${definition.slug}`) : ''}
            onChange={(value) => onChange(definition.slug, value)}
          />
        ))}
      </Box>
    </EditorSection>
  );
}

function CustomFieldControl({ definition, value, error, onChange }: { definition: CustomFieldDefinition; value: unknown; error?: string; onChange: (value: unknown) => void }) {
  const required = Boolean(Number(definition.required));
  const options = String(definition.options ?? '')
    .split('|')
    .map((option) => option.trim())
    .filter(Boolean);
  if (definition.type === 'checkbox' && options.length) {
    const current = Array.isArray(value) ? value.map(String) : value ? String(value).split(',') : [];
    return (
      <FormControl error={Boolean(error)} required={required}>
        <Typography variant="body2" color="text.secondary">{definition.name}</Typography>
        <Stack direction="row" flexWrap="wrap">
          {options.map((option) => (
            <FormControlLabel
              key={option}
              control={<Checkbox size="small" checked={current.includes(option)} onChange={(event) => onChange(event.target.checked ? [...current, option] : current.filter((entry) => entry !== option))} />}
              label={option}
            />
          ))}
        </Stack>
        {error ? <Typography variant="caption" color="error">{error}</Typography> : null}
      </FormControl>
    );
  }
  if (definition.type === 'checkbox' || definition.type === 'boolean') {
    return <FormControlLabel control={<Checkbox checked={Boolean(Number(value)) || value === true} onChange={(event) => onChange(event.target.checked ? 1 : 0)} />} label={definition.name} />;
  }
  if (definition.type === 'select' || definition.type === 'radio') {
    return (
      <TextField select fullWidth size="small" label={definition.name} required={required} value={(value ?? '') as string} error={Boolean(error)} helperText={error} onChange={(event) => onChange(event.target.value)}>
        <MenuItem value="">{t('common.none')}</MenuItem>
        {options.map((option) => <MenuItem key={option} value={option}>{option}</MenuItem>)}
      </TextField>
    );
  }
  const inputType = definition.type === 'number' ? 'number' : definition.type === 'date' ? 'date' : definition.type === 'email' ? 'email' : 'text';
  return (
    <TextField
      fullWidth
      size="small"
      label={definition.name}
      required={required}
      type={inputType}
      multiline={definition.type === 'textarea'}
      minRows={definition.type === 'textarea' ? 3 : undefined}
      value={(value ?? '') as string}
      error={Boolean(error)}
      helperText={error}
      InputLabelProps={inputType === 'date' ? { shrink: true } : undefined}
      onChange={(event) => onChange(event.target.value)}
    />
  );
}

/** Required custom fields that are still empty → validation errors. */
export function validateCustomFields(defs: CustomFieldDefinition[], values: Record<string, unknown>): ValidationErrors {
  const errors: ValidationErrors = {};
  defs.forEach((definition) => {
    if (!Number(definition.required)) return;
    const value = values[definition.slug];
    if (value == null || value === '' || (Array.isArray(value) && !value.length)) {
      errors[`extra_attributes.${definition.slug}`] = [t('catalog.validation.required', { field: definition.name })];
    }
  });
  return errors;
}

/** Location list used by the per-location stock editors. */
export function useLocations() {
  const [locations, setLocations] = useState<Array<{ value: string; label: string }>>([]);
  useEffect(() => {
    let cancelled = false;
    adminApi
      .get('locations/search')
      .then((response) => {
        const rows = Array.isArray(response.data) ? (response.data as Array<Record<string, unknown>>) : [];
        if (!cancelled) setLocations(rows.map((row) => ({ value: String(row.value ?? row.id), label: String(row.label ?? row.name ?? '') })));
      })
      .catch(() => !cancelled && setLocations([]));
    return () => {
      cancelled = true;
    };
  }, []);
  return locations;
}
